import { useCallback, useRef, useState } from 'react';

type Reconcile = (
  prev: Record<string, any>,
  next: Record<string, any>,
) => Record<string, any>;

/**
 * Custom hook for managing form data with multiple answer support.
 *
 * `reconcile` runs after every APPLICANT edit made through the handlers
 * below — not on `setFormData`, which is how saved answers are loaded. That
 * split is deliberate: edits may re-size dependent answers (client worklist
 * #81–#83), but loading must never rewrite what was saved.
 */
export const useMultipleAnswerForm = (reconcile?: Reconcile) => {
  const [formData, setFormData] = useState<Record<string, any>>({});

  // Read through a ref so a caller can pass a fresh closure each render
  // without re-creating every handler.
  const reconcileRef = useRef<Reconcile | undefined>(reconcile);
  reconcileRef.current = reconcile;

  /** setFormData for applicant edits: applies the reconcile step. */
  const updateFromEdit = useCallback(
    (fn: (prev: Record<string, any>) => Record<string, any>) => {
      setFormData((prev) => {
        const next = fn(prev);
        return reconcileRef.current ? reconcileRef.current(prev, next) : next;
      });
    },
    [],
  );

  /**
   * A drop-in for `setFormData` that counts as an applicant edit. Field
   * renderers that write the form state directly get this one, so every edit
   * they make is reconciled; `setFormData` itself stays reserved for loading
   * saved answers, which must never be rewritten.
   */
  const setFormDataFromEdit = useCallback<
    React.Dispatch<React.SetStateAction<Record<string, any>>>
  >(
    (action) =>
      updateFromEdit((prev) =>
        typeof action === "function" ? action(prev) : action,
      ),
    [updateFromEdit],
  );

  /**
   * Handle changes for single value fields
   */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    const fieldName = target.name || target.id;
    if (!fieldName) return;

    let value: string | number | boolean = target.value;

    if (target instanceof HTMLInputElement) {
      if (target.type === "checkbox") {
        value = target.checked;
      } else if (target.type === "number") {
        value = target.value === "" ? "" : Number(target.value);
      }
    }

    updateFromEdit((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  /**
   * Handle changes for multiple answer entries
   */
  const handleMultipleEntryChange = (fieldName: string, index: number, value: any) => {
    updateFromEdit((prev) => {
      const currentArray = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [];
      currentArray[index] = value;
      return {
        ...prev,
        [fieldName]: currentArray,
      };
    });
  };

  /**
   * Add a new entry for multiple answer questions.
   * Composite rows use `{}`; scalar multi-answer rows use `""`.
   */
  const handleAddEntry = (fieldName: string) => {
    updateFromEdit((prev) => {
      const current = prev[fieldName];
      const currentArray = Array.isArray(current)
        ? [...current]
        : current !== undefined && current !== null && current !== ""
          ? [current]
          : [];
      const looksComposite =
        currentArray.some(
          (entry) => entry && typeof entry === "object" && !Array.isArray(entry),
        ) ||
        (current && typeof current === "object" && !Array.isArray(current));
      currentArray.push(looksComposite ? {} : "");
      return {
        ...prev,
        [fieldName]: currentArray,
      };
    });
  };

  /**
   * Remove an entry from multiple answer questions
   */
  const handleRemoveEntry = (fieldName: string, index: number) => {
    updateFromEdit((prev) => {
      const currentArray = Array.isArray(prev[fieldName]) ? [...prev[fieldName]] : [];
      const looksComposite = currentArray.some(
        (entry) => entry && typeof entry === "object" && !Array.isArray(entry),
      );
      currentArray.splice(index, 1);
      if (currentArray.length === 0) {
        currentArray.push(looksComposite ? {} : "");
      }
      return {
        ...prev,
        [fieldName]: currentArray,
      };
    });
  };

  /**
   * Handle changes for composite field (single entry)
   */
  const handleCompositeFieldChange = (
    inputName: string,
    _compositeAnswer: Record<string, any>,
    fieldKey: string,
    value: any
  ) => {
    updateFromEdit((prev) => {
      const cur = prev[inputName];
      const base =
        typeof cur === "object" && cur !== null && !Array.isArray(cur)
          ? { ...(cur as Record<string, any>) }
          : {};
      return { ...prev, [inputName]: { ...base, [fieldKey]: value } };
    });
  };

  /**
   * Handle changes for multiple composite entries
   */
  const handleMultipleCompositeChange = (
    inputName: string,
    entryIndex: number,
    fieldKey: string,
    value: any
  ) => {
    updateFromEdit((prev) => {
      const currentArray = Array.isArray(prev[inputName]) ? [...prev[inputName]] : [];
      if (!currentArray[entryIndex]) {
        currentArray[entryIndex] = {};
      }
      const row = currentArray[entryIndex];
      const base =
        typeof row === "object" && row !== null && !Array.isArray(row)
          ? { ...(row as Record<string, any>) }
          : {};
      currentArray[entryIndex] = { ...base, [fieldKey]: value };
      return { ...prev, [inputName]: currentArray };
    });
  };

  /**
   * Format form data for submission to backend
   * Converts arrays to [{value, entryIndex}, ...] format
   */
  const formatForSubmission = (data: Record<string, any>): Record<string, any> => {
    const formattedAnswers: Record<string, any> = {};
    
    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Multiple answer entries
        formattedAnswers[key] = value.map((val, index) => ({
          value: val,
          entryIndex: index,
        }));
      } else {
        // Single answer
        formattedAnswers[key] = value;
      }
    });

    return formattedAnswers;
  };

  return {
    formData,
    setFormData,
    setFormDataFromEdit,
    handleChange,
    handleMultipleEntryChange,
    handleAddEntry,
    handleRemoveEntry,
    handleCompositeFieldChange,
    handleMultipleCompositeChange,
    formatForSubmission,
  };
};
